import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import emailApi from "../../api/emailApi";
import ListDisplayTable from "../../components/layout/dataTable/ListDisplayTable";
import { formatEnum } from "../../utils/toTitleCase";
import { getStatusColor } from "../../utils/colors";
import { Eye, Calendar, Clock, Mail } from "lucide-react";
import Modal from "../../components/ui/Modal";

const EmailHistory = ({ accountId: propAccountId = null }) => {
  const token = useSelector((s) => s.auth.access_token);
  const [data, setData] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isStandalone = !propAccountId;

  const handlePreview = async (row) => {
    try {
      setPreviewLoading(true);
      setPreviewData({
        subject: row.subject || "No Subject",
        body: row.body || "No content available",
        sentAt: row.sentAt,
        status: row.status,
        recipient: row.recipient,
      });
      setPreviewOpen(true);
    } catch (err) {
      console.error("Preview error:", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    const fetchHistory = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const endpoint = propAccountId
          ? `/email/history/${propAccountId}`
          : "/email/history";
        const res = await emailApi.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch email history:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [token, propAccountId]);

  const columns = [
    {
      header: "Date Sent",
      accessor: "sentAt",
      render: (row) => {
        const date = new Date(row.sentAt);
        return (
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-gray-400" />
            <span className="text-xs">{date.toLocaleDateString()}</span>
            <Clock size={14} className="text-gray-400 ml-2" />
            <span className="text-xs">{date.toLocaleTimeString()}</span>
          </div>
        );
      },
    },
    {
      header: "Recipient",
      accessor: "recipient",
      render: (row) => (
        <div className="flex items-center gap-2">
          <Mail size={14} className="text-gray-400" />
          <span className="text-sm">{row.recipient || "—"}</span>
        </div>
      ),
    },
    {
      header: "Subject",
      accessor: "subject",
      render: (row) => (
        <span className="text-sm font-medium">{row.subject || "No Subject"}</span>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(row.status)}`}
        >
          {formatEnum(row.status)}
        </span>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      render: (row) => (
        <button
          onClick={() => handlePreview(row)}
          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
          title="Preview"
        >
          <Eye size={16} />
        </button>
      ),
    },
  ];

  const filteredData = data.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      item.subject?.toLowerCase().includes(search) ||
      item.recipient?.toLowerCase().includes(search) ||
      item.status?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-6">
      {isStandalone && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Email History</h1>
          <p className="text-gray-600">View all sent emails and their delivery status</p>
        </div>
      )}

      {isStandalone && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by subject, recipient, or status..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-slate-200">
        <ListDisplayTable
          data={filteredData}
          columns={columns}
          loading={loading}
          emptyMessage="No email history found"
        />
      </div>

      <Modal isOpen={previewOpen} onClose={() => setPreviewOpen(false)}>
        {previewLoading ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading preview...</p>
          </div>
        ) : previewData ? (
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Email Preview</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border rounded-lg p-3 bg-gray-50">
                  <label className="text-xs font-semibold text-gray-600 uppercase">
                    Recipient
                  </label>
                  <p className="mt-1">{previewData.recipient}</p>
                </div>
                <div className="border rounded-lg p-3 bg-gray-50">
                  <label className="text-xs font-semibold text-gray-600 uppercase">
                    Status
                  </label>
                  <p className="mt-1">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(previewData.status)}`}
                    >
                      {formatEnum(previewData.status)}
                    </span>
                  </p>
                </div>
                <div className="border rounded-lg p-3 bg-gray-50 lg:col-span-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase">
                    Subject
                  </label>
                  <p className="mt-1">{previewData.subject}</p>
                </div>
              </div>
              <div className="border rounded-lg p-4 bg-white whitespace-pre-line text-sm leading-relaxed">
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-2">
                  Message
                </label>
                {previewData.body}
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default EmailHistory;
